import { decodeUtf8, encodeUtf8 } from "./utf8.ts";

/**
 * A character's name as the snapshot carries it (ADR-067, ADR-065 decision 9): two parts, the first name and
 * the family name, never joined, each at most 32 bytes of UTF-8, the session token's bound. A name is built
 * once, when its entity joins, holding both parts as text and the Name component's bytes as they travel (each
 * part's length as a `u8`, then its bytes), so the encoder copies the bytes into a frame and never encodes a
 * string in the tick. A dev join's family part is empty, and a thing has no name at all (`NO_NAME`).
 */

/**
 * The most bytes one part of a name takes.
 */
export const MAX_NAME_PART_BYTES = 32;

/**
 * The most bytes the Name component takes: a length byte and at most `MAX_NAME_PART_BYTES` for each part.
 */
export const MAX_NAME_BYTES = 2 + 2 * MAX_NAME_PART_BYTES;

/**
 * A name as an entity holds it: both parts as text, for the page and the logs, and the Name component's bytes,
 * for the encoder. It is plain data, built once and never written to.
 */
export interface EntityName {
  readonly first: string;
  readonly family: string;
  readonly bytes: Uint8Array;
}

/**
 * The name of what has none, a thing, and of a character whose name the client does not hold: two empty
 * parts. Its bytes are never written to.
 */
export const NO_NAME: EntityName = Object.freeze({ first: "", family: "", bytes: new Uint8Array(2) });

/**
 * One part's UTF-8, checked against the bound.
 *
 * @param part The part.
 * @param what The part's name, for the error.
 * @returns Its bytes.
 * @throws RangeError when the part holds a lone surrogate or takes more than `MAX_NAME_PART_BYTES`.
 */
function partBytes(part: string, what: string): Uint8Array {
  let bytes: Uint8Array;
  try {
    bytes = encodeUtf8(part);
  } catch (error) {
    throw new RangeError(`the ${what} is not text UTF-8 can carry`, { cause: error });
  }
  if (bytes.length > MAX_NAME_PART_BYTES) {
    throw new RangeError(`the ${what} takes ${bytes.length} bytes, over the ${MAX_NAME_PART_BYTES} a part may take`);
  }
  return bytes;
}

/**
 * Builds a name from its two parts, once, when its entity joins.
 *
 * @param first The first name.
 * @param family The family name, empty for a dev join or a session from before families.
 * @returns The name with its Name component's bytes.
 * @throws RangeError when a part takes more than 32 bytes of UTF-8 or holds a lone surrogate.
 * @example
 * entityName("Ada", "").bytes;
 * // Uint8Array [3, 65, 100, 97, 0]
 */
export function entityName(first: string, family: string): EntityName {
  const firstBytes = partBytes(first, "first name");
  const familyBytes = partBytes(family, "family name");
  const bytes = new Uint8Array(2 + firstBytes.length + familyBytes.length);
  bytes[0] = firstBytes.length;
  bytes.set(firstBytes, 1);
  bytes[1 + firstBytes.length] = familyBytes.length;
  bytes.set(familyBytes, 2 + firstBytes.length);
  return { first, family, bytes };
}

/**
 * Whether two names are the same, byte for byte.
 *
 * @param a One name.
 * @param b The other.
 * @returns True when their bytes are equal.
 */
export function sameName(a: EntityName, b: EntityName): boolean {
  if (a === b) {
    return true;
  }
  const x = a.bytes;
  const y = b.bytes;
  if (x.length !== y.length) {
    return false;
  }
  for (let i = 0; i < x.length; i += 1) {
    if (x[i] !== y[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Reads a Name component from a frame. The name holds a copy of its bytes, so it never keeps the frame alive.
 *
 * @param frame The frame.
 * @param offset Where the component starts.
 * @returns The name and the offset after it, or null when the frame cuts it short, a part is longer than
 *   `MAX_NAME_PART_BYTES` or a part is not well-formed UTF-8.
 */
export function readName(frame: Uint8Array, offset: number): { readonly name: EntityName; readonly end: number } | null {
  if (offset < 0 || offset >= frame.length) {
    return null;
  }
  const firstLength = frame[offset] as number;
  const familyAt = offset + 1 + firstLength;
  if (firstLength > MAX_NAME_PART_BYTES || familyAt >= frame.length) {
    return null;
  }
  const familyLength = frame[familyAt] as number;
  const end = familyAt + 1 + familyLength;
  if (familyLength > MAX_NAME_PART_BYTES || end > frame.length) {
    return null;
  }
  const first = decodeUtf8(frame, offset + 1, familyAt);
  const family = decodeUtf8(frame, familyAt + 1, end);
  if (first === null || family === null) {
    return null;
  }
  return { name: { first, family, bytes: frame.slice(offset, end) }, end };
}
